import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import billingApi from '../services/billingApi';
import { useAuth } from '../contexts/AuthContext';
import {
  CheckCircle, Clock, Loader2, ArrowRight, Receipt
} from 'lucide-react';

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeCondoId } = useAuth();
  const invoiceId = searchParams.get('invoice_id') || searchParams.get('ref');
  const status = searchParams.get('status') || 'success';

  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState<any>(null);
  const [entitlements, setEntitlements] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [entData, invData] = await Promise.all([
          billingApi.getEntitlements(activeCondoId),
          invoiceId ? billingApi.getInvoice(invoiceId) : Promise.resolve(null)
        ]);
        setEntitlements(entData);
        setInvoice(invData);
      } catch (err) {
        console.error('Error fetching payment data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [invoiceId, activeCondoId]);

  const isPending = status === 'pending' || invoice?.status === 'awaiting_payment';

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-[var(--v-surface-2)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-700)]" />
      </div>
    );
  }

  if (isPending) {
    return (
      <div className="min-h-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)]">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-center mb-8">
            <div className="w-20 h-20 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
              <Clock className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold">Pago en proceso</h1>
            <p className="text-[var(--v-text-secondary)] mt-2">
              Estamos confirmando tu pago. Esto puede tardar algunos minutos.
            </p>
          </div>

          {invoice && (
            <Card className="p-6 mb-6">
              <h3 className="font-bold mb-4">Detalles del pago</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--v-text-secondary)]">Estado</span>
                  <span className="font-medium text-amber-600">En proceso</span>
                </div>
                {invoice.payment_reference && (
                  <div className="flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Referencia</span>
                    <span className="font-mono font-bold">{invoice.payment_reference}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-[var(--v-text-secondary)]">Monto</span>
                  <span className="font-medium">
                    ${invoice.amount_charged || (invoice.amountUsd != null ? (invoice.amountUsd / 100).toFixed(2) : undefined)} {invoice.charged_currency || 'USD'}
                  </span>
                </div>
              </div>
            </Card>
          )}

          <div className="flex items-center justify-center gap-2 text-sm text-[var(--v-text-secondary)] mb-6">
            <CheckCircle className="w-4 h-4 text-[var(--v-success)]" />
            Te avisaremos por correo cuando tu pago se confirme
          </div>

          <Button variant="primary" fullWidth onClick={() => navigate('/billing')}>
            Ir a mi plan
          </Button>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)]">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold">¡Pago confirmado!</h1>
          <p className="text-[var(--v-text-secondary)] mt-2">
            Tu plan está activo. Disfruta de todas las funciones de Vecindus.
          </p>
        </div>

        {/* Receipt Summary */}
        {(invoice || entitlements) && (
          <Card className="p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Receipt className="w-5 h-5 text-[var(--v-teal-700)]" />
              <h3 className="font-bold">Resumen de tu compra</h3>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--v-text-secondary)]">Plan</span>
                <span className="font-medium capitalize">
                  {entitlements?.tier || invoice?.subscription_id || 'Home+'}
                </span>
              </div>
              {invoice && (
                <>
                  <div className="flex justify-between">
                    <span className="text-[var(--v-text-secondary)]">Monto pagado</span>
                    <span className="font-medium">
                      ${invoice.amount_charged || (invoice.amountUsd != null ? (invoice.amountUsd / 100).toFixed(2) : undefined)} {invoice.charged_currency || 'USD'}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-between">
                <span className="text-[var(--v-text-secondary)]">Estado</span>
                <span className="font-medium text-green-600 flex items-center gap-1">
                  <CheckCircle className="w-4 h-4" /> Activo
                </span>
              </div>
            </div>
          </Card>
        )}

        <Button
          variant="primary"
          size="lg"
          fullWidth
          onClick={() => navigate('/home/dashboard')}
        >
          Ir al dashboard
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>

        {invoiceId && (
          <p className="text-center text-xs text-[var(--v-text-secondary)] mt-4">
            Recibo #{invoiceId}
          </p>
        )}
      </div>
    </div>
  );
}
